import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskLines, FadeIn } from "../Reveal";
import { ASSETS } from "../../lib/site";

const PILLARS = [
  {
    title: "Operational Evolution",
    desc: "Our products are engineered by operators to eliminate long standing shortfalls found in standard-issue equipment.",
  },
  {
    title: "Beyond Specifications",
    desc: 'We exceed existing requirements to address the real-world performance gaps that "standard" gear ignores.',
  },
  {
    title: "Tactical Accountability",
    desc: "Every product in the Defense Gateway line is designed to solve a specific tactical problem.",
  },
];

const MISSION =
  'We defy "Industry Standard." All Ascendance products are born from extensive operational experience, leveraging advanced materials and new manufacturing processes that exceed your security requirements. We fix what\u2019s broken in the shooting and security world. We build mission-ready gear that mitigates the long-term flaws of traditional equipment, giving you a tactical advantage where "good enough" fails.';

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center"
    >
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={ASSETS.heroBg}
          alt="Precision engineered metal texture"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/60 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 to-transparent" />
      </motion.div>

      <div className="grid-lines absolute inset-0 z-[1] opacity-60" />

      {/* Content */}
      <motion.div style={{ opacity }} className="container-x w-full relative z-10 py-32 md:py-36">
        <FadeIn delay={0.15}>
          <div className="mb-8 md:mb-10">
            <p className="overline">Defense Innovation · Est. 2010</p>
          </div>
        </FadeIn>

        <div className="space-y-8 md:space-y-10">
          {PILLARS.map((pillar, i) => (
            <div key={pillar.title}>
              <h2 className="font-heading font-bold uppercase tracking-tight leading-[0.95] text-textmain text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
                <MaskLines
                  startDelay={0.4 + i * 0.18}
                  lines={[
                    <span key="t">
                      {pillar.title}
                      <span className="text-steel">.</span>
                    </span>,
                  ]}
                />
              </h2>
              <FadeIn delay={0.7 + i * 0.18}>
                <p className="mt-3 max-w-xl text-textmuted text-sm md:text-base leading-relaxed">
                  {pillar.desc}
                </p>
              </FadeIn>
            </div>
          ))}
        </div>

        <FadeIn delay={1.4}>
          <div className="mt-12 max-w-2xl bg-[#1b2127]/95 backdrop-blur-sm border border-white/10 border-l-2 border-l-steel p-6 md:p-8">
            <p className="text-textmuted text-base md:text-lg leading-relaxed">
              {MISSION}
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={1.6}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a href="#products" data-testid="hero-cta" className="btn-tactical group">
              Explore Our Products
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </a>
            <Link to="/about" data-testid="hero-secondary-cta" className="btn-ghost-tactical">
              Our Mission
            </Link>
          </div>
        </FadeIn>
      </motion.div>
    </section>
  );
};
